import type { SignupTerm } from '@/features/auth/types/signup-terms';
import { TermsDetailPage } from '@/pages/medical/screens/signup/TermsDetailPage';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

// US-SU-05 — dynamic Terms consent section for signup.
// Controlled: parent owns `agreedIds`; this component renders checkboxes,
// [약관 전체 동의] (Agree-to-All) row, Required/Optional badges, and [보기] link.
// Agree-to-All is *derived* (checked ⇔ every term selected), auto-syncs both ways.
// [보기] opens TermsDetailPage as a fixed modal overlay — the signup form stays
// mounted behind it, preserving all form state.
interface SignupTermsConsentProps {
	terms: SignupTerm[];
	loading: boolean;
	error: boolean;
	agreedIds: number[];
	onChange: (next: number[]) => void;
	disabled?: boolean;
}

export const SignupTermsConsent = ({
	terms,
	loading,
	error,
	agreedIds,
	onChange,
	disabled = false,
}: SignupTermsConsentProps) => {
	const { t } = useTranslation('auth');
	const [viewingTerm, setViewingTerm] = useState<SignupTerm | null>(null);

	const agreed = new Set(agreedIds);
	const allChecked =
		terms.length > 0 && terms.every((term) => agreed.has(term.termVersionId));

	const toggleOne = useCallback(
		(termVersionId: number) => {
			const next = new Set(agreed);
			if (next.has(termVersionId)) next.delete(termVersionId);
			else next.add(termVersionId);
			onChange([...next]);
		},
		[agreedIds, onChange],
	);

	const toggleAll = useCallback(() => {
		const ids = allChecked ? [] : terms.map((term) => term.termVersionId);
		onChange(ids);
	}, [allChecked, terms, onChange]);

	// Terms Detail modal overlay — renders on top of the signup form
	if (viewingTerm) {
		return (
			<TermsDetailPage term={viewingTerm} onBack={() => setViewingTerm(null)} />
		);
	}

	if (loading) {
		return (
			<div className='auth-card__terms'>
				<p className='auth-card__terms-text'>{t('signup.terms.loading')}</p>
			</div>
		);
	}

	if (error) {
		return (
			<div className='auth-card__terms'>
				<p className='auth-card__terms-text' role='alert'>
					{t('signup.terms.loadError')}
				</p>
			</div>
		);
	}

	if (terms.length === 0) {
		return (
			<div className='auth-card__terms'>
				<p className='auth-card__terms-text'>{t('signup.terms.empty')}</p>
			</div>
		);
	}

	return (
		<div className='auth-card__terms'>
			{/* Agree All */}
			<label className='auth-card__terms-all'>
				<input
					type='checkbox'
					checked={allChecked}
					onChange={toggleAll}
					disabled={disabled}
				/>
				<span>{t('signup.terms.agreeAll')}</span>
			</label>

			{/* Individual Terms */}
			<ul className='auth-card__terms-list'>
				{terms.map((term) => (
					<li key={term.termVersionId} className='auth-card__terms-item'>
						<label className='auth-card__terms-item-label'>
							<input
								type='checkbox'
								checked={agreed.has(term.termVersionId)}
								onChange={() => toggleOne(term.termVersionId)}
								disabled={disabled}
							/>
						{term.required && (
							<span className='auth-card__terms-badge--required'>
								{t('signup.terms.required')}
							</span>
						)}
							<span>{term.name}</span>
						</label>
						<button
							type='button'
							className='auth-card__terms-view'
							onClick={() => setViewingTerm(term)}
						>
							{t('signup.terms.view')}
						</button>
					</li>
				))}
			</ul>
		</div>
	);
};
